import React, { useState, useEffect } from 'react';
import { X, Building, User, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { TasksBoard, BoardUser } from '../types/board';
import { getResources, Resource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';

interface UserOption {
  type: 'business' | 'resource';
  id: string | null;
  name: string;
  email?: string;
  isAssigned: boolean;
}

interface AddUserToAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  board: TasksBoard | null;
  businessId: string;
  onUserAdded: () => void;
}

export const AddUserToAccessModal: React.FC<AddUserToAccessModalProps> = ({
  isOpen,
  onClose,
  board,
  businessId,
  onUserAdded
}) => {
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  // Load all users when modal opens
  useEffect(() => {
    if (isOpen && businessId && board) {
      loadAllUsers();
    }
  }, [isOpen, businessId, board]);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(userOptions);
    } else {
      const filtered = userOptions.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = user.name.toLowerCase().includes(searchLower);
        const emailMatch = user.email && user.email.toLowerCase().includes(searchLower);
        return nameMatch || emailMatch;
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, userOptions]);

  // Update selected users when board changes
  useEffect(() => {
    if (board) {
      const currentlyAssigned = new Set(
        board.assigned_users.map(user => getUserKey(user))
      );
      setSelectedUsers(currentlyAssigned);
    }
  }, [board]);

  const getUserKey = (user: BoardUser): string => {
    return user.type === 'business' ? 'business-null' : `resource-${user.id}`;
  };

  const loadAllUsers = async () => {
    if (!businessId || !board) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const allUsers: UserOption[] = [];
      
      // Add admin/business user
      const adminUser: UserOption = {
        type: 'business',
        id: null,
        name: 'Admin',
        isAssigned: board.assigned_users.some(user => user.type === 'business')
      };
      allUsers.push(adminUser);
      
      // Add all resource users
      const resources = await getResources(businessId);
      const activeResources = resources.filter(resource => resource.status === 'active');
      
      for (const resource of activeResources) {
        const resourceUser: UserOption = {
          type: 'resource',
          id: resource.id,
          name: `${resource.first_name} ${resource.last_name}`,
          email: resource.email,
          isAssigned: board.assigned_users.some(user => 
            user.type === 'resource' && user.id === resource.id
          )
        };
        allUsers.push(resourceUser);
      }
      
      setUserOptions(allUsers);
      setFilteredUsers(allUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserToggle = (user: UserOption) => {
    const userKey = getUserKey(user);
    const newSelected = new Set(selectedUsers);
    
    if (newSelected.has(userKey)) {
      newSelected.delete(userKey);
    } else {
      newSelected.add(userKey);
    }
    
    setSelectedUsers(newSelected);
  };

  const handleSaveChanges = async () => {
    if (!board || !supabase) return;

    setIsLoading(true);
    setError(null);
    
    try {
      // Build the new assigned users array based on selections
      const newAssignedUsers: BoardUser[] = [];
      
      for (const userKey of selectedUsers) {
        const user = userOptions.find(u => getUserKey(u) === userKey);
        if (user) {
          newAssignedUsers.push({
            type: user.type,
            id: user.id,
            name: user.name
          });
        }
      }
      
      // Ensure at least one user is assigned
      if (newAssignedUsers.length === 0) {
        setError('At least one user must be assigned to the board');
        setIsLoading(false);
        return;
      }

      // Update the board with new user assignments
      const { error } = await supabase
        .from('tasks_board')
        .update({ assigned_users: newAssignedUsers })
        .eq('id', board.id);

      if (error) throw error;

      // Reset and close
      setSearchTerm('');
      setError(null);
      onUserAdded();
      onClose();
    } catch (err) {
      console.error('Error updating board users:', err);
      setError('Failed to update board users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset to current board assignments
    if (board) {
      const currentlyAssigned = new Set(
        board.assigned_users.map(user => getUserKey(user))
      );
      setSelectedUsers(currentlyAssigned);
    }
    setSearchTerm('');
    setError(null);
    onClose();
  };

  if (!isOpen || !board) return null;

  const hasChanges = () => {
    const currentAssignments = new Set(
      board.assigned_users.map(user => getUserKey(user))
    );
    
    if (currentAssignments.size !== selectedUsers.size) return true;
    
    for (const userKey of selectedUsers) {
      if (!currentAssignments.has(userKey)) return true;
    }
    
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Board Access</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {userOptions.length === 0 ? 'No users found' : 'No users match your search'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((user) => {
                const userKey = getUserKey(user);
                const isSelected = selectedUsers.has(userKey);
                
                return (
                  <div
                    key={userKey}
                    className={`flex items-center p-3 rounded-md border cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleUserToggle(user)}
                  >
                    {/* User Icon */}
                    <div className="flex items-center mr-3">
                      {user.type === 'business' ? (
                        <Building size={16} className="text-blue-500" />
                      ) : (
                        <User size={16} className="text-green-500" />
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name}
                      </div>
                      {user.email && (
                        <div className="text-xs text-gray-500">{user.email}</div>
                      )}
                      <div className="text-xs text-gray-400">
                        {user.type === 'business' ? 'Administrator' : 'Resource'}
                      </div>
                    </div>
                    
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isLoading || !hasChanges() || selectedUsers.size === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 